import { useState } from "react";
import { Autocomplete, Box, Button, TextField, Typography } from "@mui/material";
import CheckSuggestions from "./check-suggestions";

const selectedQuoteOptions = (activity, quotes) => {
  const ids = Array.isArray(activity?.quote_ids) ? activity.quote_ids : [];
  return quotes.filter((quote) => ids.includes(quote.id));
};

/** Struttura attesa della descrizione, mostrata come guida sotto al form. */
const DESCRIPTION_SECTIONS = [
  {
    title: "Obiettivo dell’attività",
    body: "Risultato da raggiungere e motivazione dell’attività.",
  },
  {
    title: "Modalità di esecuzione",
    body: "Procedura prevista, strumenti, tecnologie, accortezze, dipendenze e verifiche necessarie.",
  },
  {
    title: "Attività svolte",
    body: "Operazioni effettuate, decisioni prese e risultati ottenuti.",
  },
  {
    title: "Utilizzo dell’AI",
    body: "Se è stata utilizzata l’intelligenza artificiale, specificare:",
    items: [
      "tool/agente: Claude, Cowork, Claude Code, GPT, Codex, OpenCode, PII, AMP o altro;",
      "modello: Fable, Opus, Sonnet, Sol, Terra, Luna o altro;",
      "attività per cui è stata utilizzata;",
      "motivo della scelta del tool e del modello;",
      "risultato ottenuto;",
      "verifiche e correzioni effettuate.",
    ],
    note: "Non è sufficiente indicare genericamente “utilizzata l’AI”. Se non è stata utilizzata, riportare “AI non utilizzata”.",
  },
  {
    title: "Problematiche incontrate",
    body: "Problemi, possibili cause, verifiche svolte, soluzioni tentate ed eventuale supporto necessario. Se non presenti, indicarlo esplicitamente.",
  },
  {
    title: "tempi",
    body: "specificare il tempo complessivo che è stato dedicato al progetto. In caso di più progetti gestiti contemporaneamente tramite AI o attività difficilmente misurabili attribuire un peso in termini di tempo da dare al progetto. L’obiettivo del tempo e funzionale esclusivamente ai consuntivi verso il cliente.",
  },
  {
    title: "Prossime attività",
    body: "Operazioni previste per la sessione successiva e relativo risultato atteso.",
  },
  {
    title: "Avanzamento del progetto",
    body: "Percentuale stimata di completamento, accompagnata da una breve motivazione.",
  },
  {
    title: "Spunti di miglioramento aziendale",
    body: "Proposte relative a tecnologie, procedure, organizzazione, automazioni o utilizzo dell’AI. Se non presenti, indicarlo esplicitamente.",
  },
];

function DescriptionGuide() {
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Cosa scrivere nella descrizione
      </Typography>
      {DESCRIPTION_SECTIONS.map((section) => (
        <Box key={section.title} sx={{ mb: 1 }}>
          <Typography variant="caption" component="p" sx={{ fontWeight: 600 }}>
            {section.title}
          </Typography>
          <Typography variant="caption" component="p" color="text.secondary">
            {section.body}
          </Typography>
          {section.items && (
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {section.items.map((item) => (
                <Typography key={item} variant="caption" component="li" color="text.secondary">
                  {item}
                </Typography>
              ))}
            </Box>
          )}
          {section.note && (
            <Typography variant="caption" component="p" color="text.secondary">
              {section.note}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}

export default function ActivityForm({
  activity,
  checking = false,
  checkResult = null,
  date,
  quotes = [],
  saving = false,
  onSave,
  onCancel,
}) {
  const [description, setDescription] = useState(activity?.description || "");
  const [selectedQuotes, setSelectedQuotes] = useState(() => selectedQuoteOptions(activity, quotes));
  const [descriptionError, setDescriptionError] = useState("");
  const [quotesError, setQuotesError] = useState("");

  // Once a save has happened -- the check is running or has already
  // answered -- there is nothing left to cancel: the record is already
  // written. Calling the exit action "Annulla" right under "Attività
  // salvata" reads as "undo the save", which is the same ambiguity the rest
  // of this screen exists to avoid. "Chiudi" says what the button actually
  // does: stop looking at this form, not undo anything. It stays enabled
  // the whole time checking is true -- that is the explicit way out of the
  // up-to-two-minute wait.
  const hasSavedOutcome = checking || Boolean(checkResult);

  const submit = (event) => {
    event.preventDefault();
    const normalizedDescription = description.trim();
    /** L'ordine dei controlli segue quello dei campi a schermo. */
    if (!selectedQuotes.length) {
      setQuotesError("Seleziona almeno una commessa");
      return;
    }
    if (!normalizedDescription) {
      setDescriptionError("La descrizione è obbligatoria");
      return;
    }

    onSave?.({
      date,
      description: normalizedDescription,
      quote_ids: [...new Set(selectedQuotes.map((quote) => quote.id))],
    });
  };

  return (
    <Box
      component="form"
      onSubmit={submit}
      noValidate
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      <Autocomplete
        multiple
        options={quotes}
        value={selectedQuotes}
        disabled={saving}
        onChange={(_, value) => {
          setSelectedQuotes(value);
          if (quotesError) setQuotesError("");
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(quote) => `${quote.code} — ${quote.title}`}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Commesse"
            error={Boolean(quotesError)}
            helperText={quotesError}
          />
        )}
      />
      <TextField
        fullWidth
        multiline
        minRows={8}
        label="Descrizione"
        value={description}
        error={Boolean(descriptionError)}
        helperText={descriptionError}
        disabled={saving}
        onChange={(event) => {
          setDescription(event.target.value);
          if (descriptionError) setDescriptionError("");
        }}
      />
      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
        <Button type="button" onClick={onCancel} disabled={saving}>
          {hasSavedOutcome ? "Chiudi" : "Annulla"}
        </Button>
        {/*
          Also disabled while checking, not only while saving: the editor
          only gets promoted from "create" to "edit" of the freshly written
          row once the check settles (see index.jsx), so a second Salva
          fired during that wait would still call create and duplicate the
          activity.
        */}
        <Button type="submit" variant="contained" disabled={saving || checking}>
          Salva
        </Button>
      </Box>
      <CheckSuggestions checking={checking} result={checkResult} />
      <DescriptionGuide />
    </Box>
  );
}
